import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "How to Ask a Business to Sponsor Your Mission Trip (Without It Feeling Like a Cold Pitch)";
const DESCRIPTION =
    "A practical guide to asking a local business to sponsor your mission trip — which businesses to approach first, what to offer in return, and a simple one-page ask that doesn't turn into a marketing negotiation.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover9.jpg',
            datePublished: '2026-07-30',
            dateModified: '2026-07-30',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-ask-a-business-to-sponsor-your-mission-trip',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Which businesses should I ask first for mission trip sponsorship?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Start with businesses where a real relationship already exists — your own employer, a family business, or a company owned by someone in your church or trip team. A warm connection converts far more often than a cold approach to a business that has never heard of you.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is a business sponsorship for a mission trip tax-deductible?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It depends on how the gift is structured and given, so don't promise a tax deduction yourself. If the business wants a deductible gift, route it through your sending church or organization's giving structure and have their finance office issue the receipt; a direct payment to you personally is not the same thing.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What should I offer a business in return for sponsoring my trip?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Keep it simple and honest: a mention of their name on your trip update page, a thank-you post after you return, or a small sign of appreciation like a card from the field. Avoid over-promising marketing exposure you can't realistically deliver.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How much should I ask a business to give?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Give a specific range tied to a real cost — for example, $150 covers one night of lodging, $500 covers a flight segment — rather than an open-ended amount. A concrete number tied to something real is easier for a business owner to say yes to quickly.",
                    },
                },
            ],
        },
    ],
};

export default function HowToAskABusinessToSponsorYourMissionTrip() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-ask-a-business-to-sponsor-your-mission-trip' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-business-sponsor-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover9.jpg" alt="A missionary handing a one-page sponsorship proposal to a local business owner" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Individual donors and churches aren't the only people who can help fund a mission trip. Local businesses give too — but the ask has to look different than a request to a friend or a congregation. A business owner is thinking about it as a decision, not just generosity, so a short, specific, low-pressure proposal works far better than an emotional appeal.</p>

                    <h2>Start with a business that already knows you</h2>

                    <p>The businesses most likely to say yes are the ones with an existing relationship to you or your trip team: your own employer, a family member's business, or a company owned by someone in your church. A warm connection means you're not explaining who you are from scratch — you're simply telling someone who already trusts you about a specific need.</p>

                    <h2>Lead with what the money actually does</h2>

                    <p>"Support my mission trip" is vague. "Cover one night of team lodging for $150" or "fund one flight segment for $500" gives a business owner something concrete to say yes to, and lets them pick an amount that fits their budget without having to ask. Break your total trip cost into a few real, named pieces before you approach anyone.</p>

                    <h2>Keep the ask to one page</h2>

                    <p>A business owner doesn't need your full testimony or trip itinerary to decide — they need four things: what the trip is, when it happens, the specific cost you're asking them to cover, and how to give. One page, printed or emailed, respects their time and makes a fast yes easier than a long story would.</p>

                    <h2>Be honest about what you can offer in return</h2>

                    <p>Some businesses give expecting nothing back; others appreciate a small visible thank-you. Offer only what you can genuinely deliver — a name mention on your trip update page, a thank-you post after you return, or a card from the field — and don't promise marketing exposure or reach you can't actually provide. Overselling the "return" turns a generosity ask into a negotiation you can't win.</p>

                    <h2>Handle the tax question carefully</h2>

                    <p>Don't tell a business their gift is tax-deductible unless you know exactly how it's being processed. A gift routed through your sending church or organization's giving structure, with their finance office issuing the receipt, is a different thing than a business handing you a check directly. See <a href="/resources/is-missionary-support-tax-deductible">is missionary support tax-deductible</a> for the distinction, and point the business to your organization's finance contact if they want a formal receipt.</p>

                    <h2>Follow up once, briefly, after the trip</h2>

                    <p>A short update after you return — one or two sentences on what their gift made possible, maybe a photo — is the single most effective thing you can do to make a business willing to give again next time. Most missionaries who lose repeat business sponsors simply forget this step, not because the ask went badly.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Which businesses should I ask first?</strong><br />
                    Start with ones where a real relationship already exists — your employer, a family business, or a church member's company.</p>

                    <p><strong>Is a business sponsorship tax-deductible?</strong><br />
                    Only if routed through your sending organization's giving structure with a proper receipt — don't promise this yourself.</p>

                    <p><strong>What should I offer in return?</strong><br />
                    Something you can genuinely deliver, like a name mention or a thank-you update — avoid over-promising exposure.</p>

                    <p><strong>How much should I ask for?</strong><br />
                    A specific amount tied to a real cost, like one night of lodging or one flight segment, not an open-ended figure.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/mission-trip-fundraising-ideas">Mission Trip Fundraising Ideas</a></li>
                        <li><a href="/resources/how-to-ask-friends-and-family-to-support-your-mission-trip">How to Ask Friends and Family to Support Your Mission Trip</a></li>
                        <li><a href="/resources/is-missionary-support-tax-deductible">Is Missionary Support Tax-Deductible?</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after hearing from missionaries who assumed businesses only fund big charities — most business owners are simply never asked, in a form that's easy to say yes to.</p>
                    <p><small><em>Last updated: 2026-07-30.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-ask-a-business-to-sponsor-your-mission-trip" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
